import jsPDF from 'jspdf';
import { buildCanonicalExportRows } from './chordProExporter.js';

/**
 * Generate and download a PDF chord sheet.
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.key
 * @param {number} opts.capo
 * @param {string} opts.lyrics  Raw lyrics text (with [Section] markers)
 * @param {Array<{chord: string, lineIndex: number, wordIndex: number}>} opts.alignment
 * @param {number} opts.transposition
 */
export function exportPDF({ title, key, capo, bpm, lyrics, alignment, transposition, strumPattern = null }) {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const marginLeft = 50;
  const marginTop = 60;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = marginTop;

  const checkPageBreak = (neededHeight = 14) => {
    if (y + neededHeight > pageHeight - 40) {
      doc.addPage();
      y = marginTop;
    }
  };

  // Title
  doc.setFont('Courier', 'bold');
  doc.setFontSize(18);
  doc.text(title || 'Chord Sheet', marginLeft, y);

  const strumCells = Array.isArray(strumPattern?.patternCells) ? strumPattern.patternCells : null;
  const strumConfidencePct = Number.isFinite(Number(strumPattern?.confidence))
    ? Math.round(Number(strumPattern.confidence) * 100)
    : null;
  const hasStrum = Array.isArray(strumCells) && strumCells.length > 0;
  if (hasStrum) {
    const countLabels = [];
    const beatsPerMeasure = Math.max(1, Math.floor(strumCells.length / 2));
    for (let b = 0; b < beatsPerMeasure; b += 1) {
      countLabels.push(String(b + 1));
      countLabels.push('+');
    }

    const panelW = 200;
    const panelX = pageWidth - marginLeft - panelW;
    const panelY = y - 12;
    const gridLeft = panelX + 64;
    const gridTop = panelY + 7;
    const gridBottom = panelY + 28;
    const slotW = Math.max(10, Math.floor((panelW - 70) / strumCells.length));
    const drawArrow = (cx, cy, dir) => {
      const shaft = 4;
      const wing = 2.2;
      doc.setDrawColor(96, 120, 210);
      doc.setLineWidth(0.8);
      if (dir === 'D') {
        doc.line(cx, cy - shaft, cx, cy + shaft);
        doc.line(cx, cy + shaft, cx - wing, cy + shaft - wing);
        doc.line(cx, cy + shaft, cx + wing, cy + shaft - wing);
      } else if (dir === 'U') {
        doc.line(cx, cy + shaft, cx, cy - shaft);
        doc.line(cx, cy - shaft, cx - wing, cy - shaft + wing);
        doc.line(cx, cy - shaft, cx + wing, cy - shaft + wing);
      } else {
        doc.setLineWidth(1.1);
        doc.line(cx - 0.5, cy, cx + 0.5, cy);
      }
    };

    // Left label
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(95, 100, 110);
    doc.text('STRUM', panelX + 4, panelY + 14);

    // Grid vertical separators
    doc.setDrawColor(205, 210, 220);
    doc.setLineWidth(0.5);
    for (let i = 1; i < strumCells.length; i += 1) {
      const x = gridLeft + i * slotW;
      doc.line(x, panelY + 4, x, panelY + 25);
    }

    // Arrow row (vector) + count row
    for (let i = 0; i < strumCells.length; i += 1) {
      const centerX = gridLeft + i * slotW + (slotW / 2);
      const dir = strumCells[i]?.dir || '';

      drawArrow(centerX, gridTop + 6, dir);

      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(130, 135, 145);
      doc.text(countLabels[i] || '', centerX, gridBottom, { align: 'center' });
    }

    if (strumConfidencePct != null) {
      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(150, 154, 162);
      const confCenterX = gridLeft + ((strumCells.length * slotW) / 2);
      doc.text(`${strumConfidencePct}% conf`, confCenterX, panelY + 41, { align: 'center' });
    }

    // Restore default text color for the rest of the document
    doc.setTextColor(0, 0, 0);
  }
  y += 28;

  // Key / BPM line, then Capo on its own line
  doc.setFont('Courier', 'normal');
  doc.setFontSize(10);
  const line1Meta = [
    key ? `Key: ${key}` : null,
    bpm && bpm > 0 ? `BPM: ${bpm}` : null,
    transposition && transposition !== 0 ? `Transposed: ${transposition > 0 ? '+' : ''}${transposition}` : null,
  ].filter(Boolean).join('  ');
  if (line1Meta) {
    doc.text(line1Meta, marginLeft, y);
    y += 16;
  }
  if (capo && capo > 0) {
    doc.text(`Capo: ${capo}`, marginLeft, y);
    y += 16;
  }

  // Divider
  doc.setDrawColor(180);
  doc.line(marginLeft, y, pageWidth - marginLeft, y);
  y += 14;

  const LYRIC_FONT_SIZE = 11;
  const CHORD_FONT_SIZE = 11;

  const rows = buildCanonicalExportRows(lyrics, alignment);
  const rowHeight = (row) => {
    if (row.type === 'blank') return 10;
    if (row.type === 'section') return 22;
    if (row.type === 'chord') return 13;
    if (row.type === 'lyric') return 16;
    return 14;
  };
  const buildRowBlocks = (inputRows) => {
    const blocks = [];
    for (let i = 0; i < inputRows.length; i += 1) {
      const row = inputRows[i];
      const next = inputRows[i + 1];
      const blockRows = [row];

      // Keep chord/lyric line pairs together.
      if (row.type === 'chord' && next?.type === 'lyric') {
        blockRows.push(next);
        i += 1;
      // Keep section headers attached to an immediate header chord.
      } else if (row.type === 'section' && next?.type === 'chord') {
        blockRows.push(next);
        i += 1;
      // Keep section headers from orphaning when directly followed by lyric.
      } else if (row.type === 'section' && next?.type === 'lyric') {
        blockRows.push(next);
        i += 1;
      }

      blocks.push({
        rows: blockRows,
        height: blockRows.reduce((sum, r) => sum + rowHeight(r), 0),
      });
    }
    return blocks;
  };

  doc.setFontSize(LYRIC_FONT_SIZE);
  const blocks = buildRowBlocks(rows);
  blocks.forEach((block) => {
    checkPageBreak(block.height + 4);
    block.rows.forEach((row) => {
      if (row.type === 'blank') {
        y += 10;
        return;
      }

      if (row.type === 'section') {
        y += 6;
        doc.setFont('Courier', 'bold');
        doc.setFontSize(LYRIC_FONT_SIZE);
        doc.text(row.text || '', marginLeft, y);
        y += 16;
        doc.setFont('Courier', 'normal');
        return;
      }

      if (row.type === 'chord') {
        doc.setFont('Courier', 'bold');
        doc.setFontSize(CHORD_FONT_SIZE);
        doc.setTextColor(30, 100, 200);
        doc.text(row.text || '', marginLeft, y);
        doc.setTextColor(0, 0, 0);
        y += 13;
        return;
      }

      if (row.type === 'lyric') {
        doc.setFont('Courier', 'normal');
        doc.setFontSize(LYRIC_FONT_SIZE);
        doc.text(row.text || '', marginLeft, y);
        y += 16;
      }
    });
  });

  doc.save(`${(title || 'chord-sheet').replace(/\s+/g, '-').toLowerCase()}.pdf`);
}
